import React, { useEffect, useRef, useState } from "react";
import OrganizationLayout from "@/Layouts/OrganizationLayout";
import LayoutIcon from "@/Components/LayoutIcon";
import { Pie } from "@/Icons";
import AppLayout from "@/Layouts/AppLayout";
import { usePage } from "@inertiajs/inertia-react";
import { addMonths, differenceInMonths } from "date-fns";
import {
  convertWeightToHumanReadable,
  dateToIndex,
} from "@/Utilities/Utilities";
import { LoadingProvider, useLoading } from "@/Contexts/LoadingContext";
import {
  chartColors,
  chartTextColors,
  DashboardBarChart,
} from "@/Utilities/V2Graphs";
import { Inertia } from "@inertiajs/inertia";

import { BudgetChart } from "@/Utilities/V2Graphs";
import { useMemo } from "react";
import { SCOPE_OPTIONS } from "@/Enums/scopes";
import { trend } from "@/Utilities/Utilities";
import { usePayload } from "@/Contexts/PayloadContext";
import { toast } from "react-toastify";
import { Formheader } from "@/Components";
import { debounce } from "lodash";

const STEPS = {
  WAITING: 1,
  FETCH_GRAPH_SCOPE_DATA: 2,
  GENERATING_SCOPE_GRAPH: 3,
  GENERATING_YEARLY_GRAPH: 4,
  GENERATING_BUDGET_GRAPH: 5,
  GENERATING_WORD_TEMPLATE: 6,
  DOWNLOADING_FILE: 7,
  FINISHED: 8,
};

const StepDescriptions = {
  [STEPS.WAITING]: "Waiting",
  [STEPS.FETCH_GRAPH_SCOPE_DATA]: "Fetching data for scope graphs",
  [STEPS.GENERATING_SCOPE_GRAPH]: "Generating scope graphs",
  [STEPS.GENERATING_YEARLY_GRAPH]: "Generating yearly emission graphs",
  [STEPS.GENERATING_BUDGET_GRAPH]: "Generating budget graphs",
  [STEPS.GENERATING_WORD_TEMPLATE]: "Creating inventory word file",
  [STEPS.DOWNLOADING_FILE]: "Downloading file",
  [STEPS.FINISHED]: "Finished",
};

const getChartColor = (index, label) => {
  let i =
    {
      "Scope 1": 0,
      "Scope 2": 1,
      "Scope 3": 2,
      "Out of scope": -2,
      Trend: 4,
      "Paris Agreement": -1,
      "Uncategorized emissions": -1,
    }[label] || index % chartColors.length;
  if (i === -1) return "#DDEBFC"; // color not in normal graph colors...
  if (i === -2) return "#C4C4C4"; // color not in normal graph colors...
  return chartColors[i];
};

export default function BuildSteps() {
  const { payload, reset } = usePayload();

  const [step, setStep] = useState(STEPS.WAITING);

  function createFormData(payload) {
    /**
     * Convert JSON payload into FormData
     * Supports:
     * - base64 images conversion
     * - array conversion
     * - regular key value pairs
     *
     * @param payload
     * @returns {FormData}
     */
    const formData = new FormData();
    for (const key in payload) {
      if (payload.hasOwnProperty(key)) {
        if (
          typeof payload[key] === "string" &&
          payload[key].startsWith("data:image")
        ) {
          // Convert base64 to Blob
          const binaryString = window.atob(payload[key].split(",")[1]);
          const len = binaryString.length;
          const bytes = new Uint8Array(len);
          for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes.buffer], { type: "image/png" });
          formData.append(key, blob);
        } else if (payload[key] instanceof File) {
          formData.append(key, payload[key], payload[key].name);
        } else if (
          typeof payload[key] === "object" &&
          Array.isArray(payload[key])
        ) {
          payload[key].forEach((item) => {
            formData.append(key + "[]", item);
          });
        } else {
          formData.append(key, payload[key]);
        }
      }
    }
    return formData;
  }

  useEffect(() => {
    // validate payload
    if (!payload?.inventory_year || !payload?.base_year) {
      Inertia.visit(route("emissions.inventory.setup"));
    }

    if (step === STEPS.WAITING) {
      setStep(STEPS.FETCH_GRAPH_SCOPE_DATA);
      Inertia.reload({
        only: ["graph_data"],
        onSuccess: () => {
          setStep(STEPS.GENERATING_SCOPE_GRAPH);
        },
      });
    } else if (step === STEPS.GENERATING_WORD_TEMPLATE) {
      axios({
        method: "post",
        url: route("emissions.inventory.export"),
        data: createFormData(payload),
        responseType: "blob",
      })
        .then((response) => {
          setStep(STEPS.DOWNLOADING_FILE);

          setTimeout(() => {
            setStep(STEPS.FINISHED);
            reset();

            // Create a Blob from the PDF Stream
            const file = new Blob([response.data], {
              type: "application/octet-stream",
            });
            // Build a URL from the file
            const fileURL = URL.createObjectURL(file);
            // Open the URL on new Window
            const link = document.createElement("a");
            link.href = fileURL;
            link.setAttribute("download", `${payload.inventory_name}.docx`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast.success("Successfully download inventory Word-file.", {
              autoClose: 5000,
            });
          }, 1000);
        })
        .catch((error) => {
          toast.error("An error occurred while generating the file.", {
            autoClose: 5000,
          });
          reset();
        });
    }
  }, [step]);

  const currentStepPercent =
    step === STEPS.WAITING ? 0 : (step / Object.keys(STEPS).length) * 100;

  return (
    <LoadingProvider>
      <div className="mb-16 flex flex-col gap-4">
        <Formheader className="mb-2">Processing Your Request</Formheader>
        <p>
          We're currently working on your request. This might take a couple of
          minutes. During this time, it's important that you stay on this page.
          We're fetching all the necessary data and creating a Word file for
          you. We appreciate your patience.
        </p>
        <div className="space-y-5">
          <div
            className="flex h-4 w-full overflow-hidden rounded-full bg-gray"
            role="progressbar"
            aria-valuenow={currentStepPercent}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div
              className="flex flex-col justify-center overflow-hidden whitespace-nowrap rounded-full bg-primary text-center text-xs text-white transition-[width] delay-500 duration-1000"
              style={{
                width: `${currentStepPercent}%`,
                backgroundImage:
                  "linear-gradient(90deg, #1f4444 25%, #209595 50%, #1f4444 75%)",
                backgroundSize: "200% 100%",
                backgroundPosition: "200% 0",
                animation: "loading 1.5s infinite",
              }}
            >
              {Math.round(currentStepPercent)}%
            </div>
          </div>

          <p>
            Step {step} of {Object.keys(STEPS).length}: {StepDescriptions[step]}
          </p>
        </div>

        {step === STEPS.GENERATING_SCOPE_GRAPH && (
          <div className="absolute -z-40" style={{ width: "19cm" }}>
            <GenerateScopeGraphs
              onNext={() => setStep(STEPS.GENERATING_YEARLY_GRAPH)}
            />
          </div>
        )}
        {step === STEPS.GENERATING_YEARLY_GRAPH && (
          <div className="absolute -z-40" style={{ width: "19cm" }}>
            <GenerateYearlyGraph
              onNext={() => setStep(STEPS.GENERATING_BUDGET_GRAPH)}
            />
          </div>
        )}
        {step === STEPS.GENERATING_BUDGET_GRAPH && (
          <div className="absolute -z-40" style={{ width: "25cm" }}>
            <Budget onNext={() => setStep(STEPS.GENERATING_WORD_TEMPLATE)} />
          </div>
        )}
      </div>
    </LoadingProvider>
  );
}

BuildSteps.layout = (page) => (
  <AppLayout title="Emission inventory">
    <OrganizationLayout
      header="Emission inventory"
      icon={
        <LayoutIcon>
          <Pie size={50} className="text-black" />
        </LayoutIcon>
      }
    >
      {page}
    </OrganizationLayout>
  </AppLayout>
);

function GenerateScopeGraphs({ onNext = () => null }) {
  const { payload, onFormChange } = usePayload();

  const questions = usePage().props?.graph_data?.questions?.data || [];
  const answers = usePage().props?.graph_data?.answers || [];
  const teams = usePage().props?.graph_data?.teams?.data || [];

  const startDate = new Date(`${payload.inventory_year}-01-01`);
  const endDate = new Date(`${payload.inventory_year}-12-31`);

  const monthDiff = differenceInMonths(endDate, startDate) + 1;
  const data = [];

  let lastYear = null;

  for (let i = 0; i < monthDiff; i++) {
    const month = addMonths(startDate, i);
    const year = month.getFullYear();
    const monthName = month.toLocaleString("en-US", { month: "short" });
    let name = `${monthName}`;

    if (year !== lastYear) {
      name = `${year} ${monthName}`;
      lastYear = year;
    }

    data.push({
      key: dateToIndex(month),
      name: name,
    });
  }

  const labels = data;

  // Preprocess teams and questions for direct access
  const teamMap = new Map(teams.map((team) => [team.id, team.name]));
  const questionMap = new Map(questions.map((q) => [q.id, q.title]));

  const entries = {
    teams: {},
    questions: {},
  };

  Object.entries(answers).forEach(([question_id, answer]) => {
    if (!answer.has_emission) return;

    // Handling questions
    if (!entries.questions.hasOwnProperty(question_id)) {
      entries.questions[question_id] = {
        label: questionMap.get(Number(question_id)),
        data: {},
        scientific_uncertainty: answer.scientific_uncertainty,
      };
    }

    const questionData = entries.questions[question_id].data;

    Object.entries(answer.teams).forEach(([team_id, team_info]) => {
      // Handling teams
      if (!entries.teams.hasOwnProperty(team_id)) {
        entries.teams[team_id] = {
          label: teamMap.get(Number(team_id)),
          data: {},
        };
      }

      const teamData = entries.teams[team_id].data;

      // Proportional emissions
      Object.entries(team_info.proportional_emission).forEach(
        ([key, value]) => {
          questionData[key] = (questionData[key] || 0) + value;
        },
      );

      // Monthly emissions
      Object.entries(team_info.monthly_emission).forEach(
        ([year_month, emission]) => {
          teamData[year_month] = teamData[year_month] || {};

          Object.entries(emission).forEach(([key, value]) => {
            teamData[year_month][key] =
              (teamData[year_month][key] || 0) + value;
          });
        },
      );
    });
  });

  const dataSetsPerScope =
    SCOPE_OPTIONS.map((scope, i) => {
      if (
        (scope.value === "scope_3" && payload?.scope_3_emissions === "no") ||
        (scope.value === "no_scope" &&
          payload?.biogenic_combustion_emissions === "no")
      ) {
        return;
      }

      return {
        label: scope.label,
        data: labels.map((label) => {
          return {
            y: Object.keys(entries.teams).reduce((acc, team_id) => {
              return (
                acc +
                (entries.teams[team_id].data[label.key]?.[scope.value] || 0)
              );
            }, 0),
          };
        }),
        barPercentage: 0.5,
        borderColor: getChartColor(i, scope.label),
        backgroundColor: getChartColor(i, scope.label),
      };
    })?.filter((dataSet) => dataSet) || [];

  const getImage = (context) => {
    // get base64 image from context.canvas
    const base64Image = context.canvas.toDataURL("image/png");

    onFormChange({
      target: {
        name: "graph_image",
        value: base64Image,
      },
    });
    onNext();
  };

  const captureImage = useMemo(() => {
    return debounce((context) => getImage(context), 2500);
  }, []);

  return (
    <DashboardBarChart
      onChartUpdated={(context) => captureImage(context)}
      dataSets={dataSetsPerScope}
      labels={labels.map((l) => l.name)}
      disableAnimations={true}
      showTotalAboveBar={true}
    />
  );
}

function GenerateYearlyGraph({ onNext = () => null }) {
  const { payload, onFormChange } = usePayload();

  const questions = usePage().props?.graph_data?.questions?.data || [];
  const answers = usePage().props?.graph_data?.answers || [];
  const teams = usePage().props?.graph_data?.teams?.data || [];

  // Start date is the first day of the base year
  const startDate = new Date(`${payload.base_year}-01-01`);
  // Assume the end date is 10 years from the start date for demonstration
  const endDate = new Date(`${payload.inventory_year}-12-31`);

  // Calculate the number of years between start and end date
  const yearDiff = endDate.getFullYear() - startDate.getFullYear() + 1;
  const data = [];

  // Generate data only for each year
  for (let i = 0; i < yearDiff; i++) {
    const yearDate = new Date(startDate.getFullYear() + i, 0, 1); // January 1st of each year
    data.push({
      key: yearDate.getFullYear(),
      name: yearDate.getFullYear().toString(),
    });
  }

  const labels = data;

  // Preprocess teams and questions for direct access
  const teamMap = new Map(teams.map((team) => [team.id, team.name]));
  const questionMap = new Map(questions.map((q) => [q.id, q.title]));

  const entries = {
    teams: {},
    questions: {},
  };

  Object.entries(answers).forEach(([question_id, answer]) => {
    if (!answer.has_emission) return;

    // Handling questions
    if (!entries.questions.hasOwnProperty(question_id)) {
      entries.questions[question_id] = {
        label: questionMap.get(Number(question_id)),
        data: {},
        scientific_uncertainty: answer.scientific_uncertainty,
      };
    }

    const questionData = entries.questions[question_id].data;

    Object.entries(answer.teams).forEach(([team_id, team_info]) => {
      // Handling teams
      if (!entries.teams.hasOwnProperty(team_id)) {
        entries.teams[team_id] = {
          label: teamMap.get(Number(team_id)),
          data: {},
        };
      }

      const teamData = entries.teams[team_id].data;

      // Proportional emissions
      Object.entries(team_info.proportional_emission).forEach(
        ([key, value]) => {
          questionData[key] = (questionData[key] || 0) + value;
        },
      );

      // Yearly emissions instead of monthly
      Object.entries(team_info.monthly_emission).forEach(
        ([year_month, emission]) => {
          const year = year_month.split("_")[0];
          teamData[year] = teamData[year] || {};

          Object.entries(emission).forEach(([key, value]) => {
            teamData[year][key] = (teamData[year][key] || 0) + value;
          });
        },
      );
    });
  });

  const dataSetsPerScope =
    SCOPE_OPTIONS.map((scope, i) => {
      if (
        (scope.value === "scope_3" && payload?.scope_3_emissions === "no") ||
        (scope.value === "no_scope" &&
          payload?.biogenic_combustion_emissions === "no")
      ) {
        return;
      }

      return {
        label: scope.label,
        data: labels.map((label) => {
          return {
            y: Object.keys(entries.teams).reduce((acc, team_id) => {
              return (
                acc +
                (entries.teams[team_id].data[label.key]?.[scope.value] || 0)
              );
            }, 0),
          };
        }),
        barPercentage: 0.17,
        borderColor: getChartColor(i, scope.label),
        backgroundColor: getChartColor(i, scope.label),
      };
    })?.filter((dataSet) => dataSet) || [];

  const getImage = (context) => {
    // get base64 image from context.canvas
    const base64Image = context.canvas.toDataURL("image/png");

    onFormChange({
      target: {
        name: "yearly_graph_image",
        value: base64Image,
      },
    });
    onNext();
  };

  const captureImage = useMemo(() => {
    return debounce((context) => getImage(context), 2500);
  }, []);

  return (
    <DashboardBarChart
      onChartUpdated={(context) => captureImage(context)}
      dataSets={dataSetsPerScope}
      labels={labels.map((l) => l.name)}
      disableAnimations={true}
      showTotalAboveBar={true}
    />
  );
}

export const Budget = ({ onNext = () => null }) => {
  const { payload, setPayload } = usePayload();
  const { isLoaded, setIsLoaded } = useLoading();

  // Don't show teams with less than 6 years reported, since we need at least 5 years not including this year for the graph
  const teams =
    usePage().props?.graph_data?.teams?.data?.filter(
      (team) => team.years_reported >= 5,
    ) || [];

  const [emmaData, setEmmaData] = useState({});
  const [budgetData, setBudgetData] = useState([]);

  const currentYear = new Date().getFullYear();
  const years = Object.keys(withoutThisYear(emmaData));
  const yearsReported = years.length;
  const initialYear = useRef(Number(years?.[0] ?? currentYear));
  const labels = Array.from(
    { length: 2051 - initialYear.current },
    (_, i) => initialYear.current + i,
  ); // [..., 2019, 2020, ..., 2050]

  /**
   * Fetch data from API for the selected teams and budget data for all municipalities
   */
  const fetchBudgetData = () => {
    setIsLoaded(false);

    Promise.all([
      // Fetch municipalities budget data if not already loaded
      budgetData.length === 0
        ? fetch(route("budget.database"))
            .then((response) => response.json())
            .then((data) => {
              setBudgetData(data);
            })
            .catch((error) => console.error(error))
        : null,

      // Get selected teams reported data
      fetch(route("budget.data", { teamFilter: teams.map((team) => team.id) }))
        .then((response) => response.json())
        .then((data) => {
          // console.log('data', data);
          setEmmaData(data.yearly);
          const years = Object.keys(withoutThisYear(data.yearly));
          initialYear.current = Number(years?.[0] ?? currentYear);
        })
        .catch((error) => console.error(error)),
    ]).then(() => {
      setIsLoaded(true);
    });
  };

  useEffect(() => {
    fetchBudgetData();
  }, []);

  // get each unique municipality from teams
  const municipalities = useMemo(
    () =>
      teams.reduce((acc, team) => {
        if (!acc.includes(team.municipality)) {
          acc.push(team.municipality);
        }
        return acc;
      }, []),
    teams,
  );

  /**
   * Remove current year key from an object
   *
   * @param data
   * @returns {any}
   */
  function withoutThisYear(data = {}) {
    const clone = JSON.parse(JSON.stringify(data));
    delete clone[currentYear];
    return clone;
  }

  /**
   * Calculate which percentage we should use based on the selected municipality, average value if more is selected
   *
   * @param municipalities
   * @returns {number}
   */
  const getBudgetPercentageFromSelectedMunicipalities = (municipalities) => {
    let percentage = 0;
    let count = 0;
    municipalities.forEach((municipality) => {
      const municipalityData = budgetData.find(
        (data) => data.Name === municipality,
      );
      if (municipalityData) {
        percentage += municipalityData.EmissionChangePercent;
        count++;
      }
    });

    return percentage / (count || 1);
  };

  /**
   * Calculate the total emission for the selected year after filtering the scopes
   *
   * @param data
   * @returns {number}
   */
  function calculateTotalAfterFilteringScope(data = {}) {
    // loop SCOPE_OPTIONS and check if it is in scopeFilter then add its value to the total
    let total = 0;
    SCOPE_OPTIONS.forEach((scope) => {
      if (
        (scope.value === "scope_3" && payload?.scope_3_emissions === "no") ||
        (scope.value === "no_scope" &&
          payload?.biogenic_combustion_emissions === "no")
      ) {
        return;
      }

      total += data?.[scope.value] || 0;
    });

    return total;
  }

  /**
   * Calculate the trend for this year based on the previous reported data
   *
   * @param existingData
   * @param year
   * @returns {total: number, scope1: number, scope2: number, scope3: number}
   */
  function calculateEmissionForYear(
    existingData,
    year = new Date().getFullYear(),
  ) {
    const keys = Object.keys(withoutThisYear(existingData)).map(Number);

    let index = keys.findIndex((k) => k === year);

    if (index === -1) {
      // If the year is not in the data, we must calculate the theoretical index
      let latestYear = Math.max(...keys);
      index = keys.length + (year - latestYear - 1);
    }

    const data = {};

    SCOPE_OPTIONS.forEach((scope) => {
      const scopeData = keys.map((year) => existingData[year][scope.value]);
      const scopeTrendLine = trend(
        keys.map((_, i) => i),
        scopeData,
      );

      // Set the projection of current year emission
      data[scope.value] =
        scopeTrendLine.intercept + scopeTrendLine.slope * index;
    });

    data.total = calculateTotalAfterFilteringScope(data);

    return data;
  }

  /**
   * Build the datasets for the graph
   * @returns {*[]}
   */
  function buildDataSets() {
    let cloneEmmaData = withoutThisYear(emmaData);

    const budgetCurve = (total, percentage, years, padding) => {
      const data = [];
      for (let i = 0; i < years; i++) {
        if (i + 1 < padding) {
          data.push(null);
          continue;
        }
        data.push(total);
        total = total - total * percentage;
      }
      return data;
    };

    // We change our labels for the linear regression to start from 0 and increment by 1 for each data point
    const trendLine = trend(
      years.map((_, i) => i),
      years.map((year) => calculateTotalAfterFilteringScope(emmaData[year])),
    );

    // Generate the y values for the trend line for every year
    let trendData = labels.map((year, index) => {
      if (index < yearsReported) {
        return null;
      } else {
        const trendValue = trendLine.intercept + trendLine.slope * index;
        if (trendValue < 0) {
          if (belowZeroIndex.current === false) {
            belowZeroIndex.current = index;
          }
          return null;
        }

        return trendValue;
      }
    });

    let datasets = [];

    // Add the trend of the current year to the dataset, so it can be visualized in the graph
    cloneEmmaData[currentYear] = calculateEmissionForYear(cloneEmmaData);

    SCOPE_OPTIONS.forEach((scope, index) => {
      if (
        (scope.value === "scope_3" && payload?.scope_3_emissions === "no") ||
        (scope.value === "no_scope" &&
          payload?.biogenic_combustion_emissions === "no")
      ) {
        return;
      }

      datasets.push({
        label: scope.label,
        data: Object.keys(cloneEmmaData).map(
          (year) => cloneEmmaData[year][scope.value],
        ),
        borderColor: getChartColor(index, scope.label),
        backgroundColor: getChartColor(index, scope.label),
        fill: "origin",
        stack: "none",
        pointRadius: 0,
        segment: {
          // borderColor: (ctx) => {
          //   if (
          //     ctx.p0DataIndex === yearsReported - 1 ||
          //     ctx.p1DataIndex === yearsReported
          //   ) {
          //     // Custom fill color for the segment between last year and current year
          //     return ["#94dcde", "#fbb2a3", "#a37ab6", "#dbe2ea"][index % 4];
          //   }
          //   return chartColors[index % chartColors.length];
          // },
          // backgroundColor: (ctx) => {
          //   if (
          //     ctx.p0DataIndex === yearsReported - 1 ||
          //     ctx.p1DataIndex === yearsReported
          //   ) {
          //     // Custom fill color for the segment between last year and current year
          //     return ["#94dcde", "#fbb2a3", "#a37ab6", "#dbe2ea"][index];
          //   }
          //   return chartColors[index % chartColors.length];
          // },
        },
      });
    });

    const createDiagonalPattern = (color = "black") => {
      let shape = document.createElement("canvas");
      shape.width = 16;
      shape.height = 16;
      let c = shape.getContext("2d");
      c.strokeStyle = color;
      c.lineWidth = 5;

      c.beginPath();
      c.moveTo(16, 0);
      c.lineTo(0, 16);
      c.stroke();

      c.beginPath();
      c.moveTo(0, 0);
      c.lineTo(2, 2);
      c.stroke();

      c.beginPath();
      c.moveTo(16, 16);
      c.lineTo(14, 14);
      c.stroke();

      return c.createPattern(shape, "repeat");
    };

    // Reverse order to get Scope 1 at top and Out of scope in the bottom of the graph
    datasets = datasets.reverse();

    datasets.push({
      label: "Trend",
      borderColor: "#868686",
      borderWidth: 2,
      borderDash: [5, 5],
      // borderDash: [5, 5],
      backgroundColor: createDiagonalPattern("rgba(102, 142, 159, 0.2)"),
      data: trendData,
      fill: "origin",
      pointRadius: 0,
      stack: "single",
      // order: 1,
      // trendTitle:
    });

    datasets.push({
      label: "Paris Agreement",
      data:
        years &&
        budgetCurve(
          calculateTotalAfterFilteringScope(
            emmaData?.[years?.[years.length - 1]],
          ),
          municipalityPercentage / 100,
          labels.length,
          yearsReported,
        ),
      borderColor: getChartColor(4, "Paris Agreement"),
      backgroundColor: getChartColor(4, "Paris Agreement"),
      fill: true,
      pointRadius: 0,
      // order: 10,
    });

    return datasets;
  }

  let datasets = [];
  let percentageOver = 0;
  let municipalityPercentage = 0;
  let exceedYear = null;
  let allocation = 0;
  let totalBudget = 0;
  let currentYearTotalBudget = 0;
  const belowZeroIndex = useRef(false);
  let yearlyBudgets = {};

  if (isLoaded && teams.length > 0 && municipalities.length > 0) {
    municipalityPercentage =
      getBudgetPercentageFromSelectedMunicipalities(municipalities);

    // We must check which years are relevant based on the emmaData years

    let teamsTotalReported = 0;
    // let municipalitiesHistoricalEmission = 0;
    let municipalitiesTotalBudget = 0;
    let municipalitiesCount = 0;
    let municipalitiesHistoricalEmission = {};

    let budgetYearsAvailable = [];
    municipalities.forEach((municipality) => {
      const municipalityData = budgetData.find(
        (data) => data.Name === municipality,
      );
      if (municipalityData) {
        municipalitiesTotalBudget += municipalityData.Budget;

        /**
         * HistoricalEmissions is an objects with key year and its value
         * - We must check which years are relevant based on the emmaData years
         * - Then if it's a year that is relevant, add it to the total
         */
        municipalityData.HistoricalEmission.forEach((row) => {
          if (years.includes(row.year.toString())) {
            // municipalitiesHistoricalEmission += row.CO2Equivalent;

            // Append the budget data to municipalitiesBudgetData with the year as key, and the value as value (multiplied by 1000)

            if (!municipalitiesHistoricalEmission[row.year]) {
              municipalitiesHistoricalEmission[row.year] = 0;
            }

            municipalitiesHistoricalEmission[row.year] += row.value;

            if (!budgetYearsAvailable.includes(row.year)) {
              budgetYearsAvailable.push(row.year);
            }
          }
        });

        municipalitiesCount++;
      }
    });

    // Add relevant total from emmaData to teamsTotalReported based on municipality budget years
    budgetYearsAvailable.forEach((year) => {
      teamsTotalReported += emmaData[year].total;
    });

    /**
     * The municipalities HistoricalEmission is reported after 2 years so we must calculate this years budget based on the trend
     * - Calculate the trend for municipalitiesBudgetData so its possible to calculate the budget for this year
     * - Calculate what the teams budget for this year based on the trend data
     */
    const trendLine = trend(
      Object.keys(municipalitiesHistoricalEmission).map((year) => Number(year)),
      Object.values(municipalitiesHistoricalEmission),
    );

    // get last key in municipalitiesBudgetData
    const lastReportedBudgetYear = Object.keys(
      municipalitiesHistoricalEmission,
    ).pop();
    for (let i = Number(lastReportedBudgetYear); i < currentYear; i++) {
      municipalitiesHistoricalEmission[i] =
        municipalitiesHistoricalEmission[i - 1] + trendLine.slope;
    }

    const totalMunicipalitiesHistoricalEmission = Object.values(
      municipalitiesHistoricalEmission,
    ).reduce((acc, value) => acc + value, 0);

    /**
     * To get the allocation divide the teams total emissions by the municipalities total emissions
     * - Then divide by 1000 to get the allocation per ton
     */
    allocation =
      teamsTotalReported /
      1000 /
      (totalMunicipalitiesHistoricalEmission / (municipalitiesCount || 1));
    totalBudget = allocation * municipalitiesTotalBudget;

    const reductionRate = 1 - municipalityPercentage / 100;

    // Calculate the amount for the first year
    currentYearTotalBudget =
      (totalBudget * (1 - reductionRate)) /
      (1 - Math.pow(reductionRate, 2050 - currentYear + 1));

    let currentYearBudget = currentYearTotalBudget;

    for (let year = currentYear; year <= 2050; year++) {
      yearlyBudgets[year] = currentYearBudget;
      currentYearBudget *= reductionRate;
    }

    datasets = buildDataSets();

    let totalEmission = 0;
    let yearlyEmission = {};

    // let lastYearTotal = emmaData[years[years.length - 1]]?.total || 0;
    for (let year = currentYear; year <= 2050; year++) {
      let emissionForYear;

      emissionForYear = calculateEmissionForYear(
        withoutThisYear(emmaData),
        year,
      );

      yearlyEmission[year] = emissionForYear.total / 1000; // Convert from KG to tonnes
      totalEmission += yearlyEmission[year] > 0 ? yearlyEmission[year] : 0;

      if (exceedYear === null && totalEmission > totalBudget) {
        exceedYear = year;
      }
    }

    // remove years after we exceed the budget on future graph
    if (exceedYear !== null && exceedYear > currentYear) {
      for (
        let dataSetIndex = 0;
        dataSetIndex < datasets.length;
        dataSetIndex++
      ) {
        for (
          let dataIndex = exceedYear - initialYear.current;
          dataIndex < datasets[dataSetIndex].data.length;
          dataIndex++
        ) {
          if (datasets[dataSetIndex].label !== "Trend") {
            // only cut trend in other graphs
            continue;
          }
          datasets[dataSetIndex].data[dataIndex] = null;
        }
      }
    }

    percentageOver = ((totalEmission - totalBudget) / totalBudget) * 100;
  }

  const getImage = (context, totalBudget, exceedYear, percentageOver) => {
    // get base64 image from context.canvas
    const base64Image = context.canvas.toDataURL("image/png");

    const reportedYears = Array.from(
      { length: 2051 - initialYear.current },
      (_, i) => initialYear.current + i,
    ); // [..., 2019, 2020, ..., 2050]

    let textString = `The total budget until year 2050 is ${convertWeightToHumanReadable(totalBudget * 1000, true)?.concat} CO₂e.\n`;

    if (exceedYear) {
      textString += `Historic and current emission trends indicate that your team will never meet the 2050 budget target.\n`;
      textString += `If the trend continues, your budget ends ${exceedYear} and you will exceed the budget with ${Math.round(percentageOver)}% in 2050.\n`;
    } else {
      textString += `Historic and current emission trends indicate that your team is in line with the Paris Agreement reduction targets. If the trend continues, you will reach the budget goal by ${belowZeroIndex.current !== false ? reportedYears[belowZeroIndex.current] : null}.\n`;
    }

    setPayload((prev) => ({
      ...prev,
      budget_text: textString,
      graph_budget_image: base64Image,
    }));

    onNext();
  };

  const captureImage = useMemo(() => {
    return debounce(
      (context, totalBudget, exceedYear, percentageOver) =>
        getImage(context, totalBudget, exceedYear, percentageOver),
      2500,
    );
  }, []);

  return (
    <div className="w-full">
      <BudgetChart
        onChartUpdated={(context) =>
          captureImage(context, totalBudget, exceedYear, percentageOver)
        }
        dataSets={datasets}
        labels={labels}
        exceedYear={exceedYear}
        disableAnimations={true}
      />
    </div>
  );
};
